import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Freebie, FreebieRequest } from '@/types/freebie';

const FREEBIES_COLLECTION = 'freebies';
const FREEBIE_REQUESTS_COLLECTION = 'freebieRequests';

const firestoreDb: typeof db = db;

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const freebiesService = {
  async create(freebie: Omit<Freebie, 'id' | 'createdAt' | 'updatedAt' | 'slug'>): Promise<string> {
    const cleanName = freebie.name.trim();
    if (!cleanName) throw new Error('Freebie name is required');

    const payload = {
      ...freebie,
      name: cleanName,
      slug: slugify(cleanName),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(firestoreDb, FREEBIES_COLLECTION), payload);
    return docRef.id;
  },

  async update(id: string, freebie: Partial<Freebie>): Promise<void> {
    const sanitized = Object.fromEntries(
      Object.entries(freebie).filter(([, value]) => value !== undefined)
    ) as Partial<Freebie>;

    const payload: Record<string, unknown> = {
      ...sanitized,
      updatedAt: Timestamp.now(),
    };

    if (typeof sanitized.name === 'string') {
      const cleanName = sanitized.name.trim();
      payload.name = cleanName;
      payload.slug = slugify(cleanName);
    }

    await updateDoc(doc(firestoreDb, FREEBIES_COLLECTION, id), payload);
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(firestoreDb, FREEBIES_COLLECTION, id));
  },

  async getAll(): Promise<Freebie[]> {
    const q = query(collection(firestoreDb, FREEBIES_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data(),
      createdAt: item.data().createdAt?.toDate(),
      updatedAt: item.data().updatedAt?.toDate(),
    })) as Freebie[];
  },

  async getActive(): Promise<Freebie[]> {
    const q = query(collection(firestoreDb, FREEBIES_COLLECTION), where('isActive', '==', true));
    const snapshot = await getDocs(q);
    const freebies = snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data(),
      createdAt: item.data().createdAt?.toDate(),
      updatedAt: item.data().updatedAt?.toDate(),
    })) as Freebie[];
    return freebies.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
  },

  async getBySlug(slug: string): Promise<Freebie | null> {
    const q = query(collection(firestoreDb, FREEBIES_COLLECTION), where('slug', '==', slug));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const item = snapshot.docs[0];
    return {
      id: item.id,
      ...item.data(),
      createdAt: item.data().createdAt?.toDate(),
      updatedAt: item.data().updatedAt?.toDate(),
    } as Freebie;
  },

  async submitRequest(request: Omit<FreebieRequest, 'id' | 'createdAt'>): Promise<void> {
    await addDoc(collection(firestoreDb, FREEBIE_REQUESTS_COLLECTION), {
      ...request,
      createdAt: Timestamp.now(),
    });
  },
};
